import isEmpty from "lodash/isEmpty";
import { PaginatedResponse } from "../../types";
import {
  DistinctFilterMetadata,
  FilterMetadata,
  Fund,
  FundDefferedLoadsManager,
  FundOverlapMatrixResponse,
  FundPerformance,
  FundPortfolio,
  FundRanking,
  HistoricalNav,
  FundStockHolding,
  NFO,
} from "../../types/fund";
import { api } from "../axios";

export interface GetFundsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Fund[];
  total: number;
}

export function getFunds(
  pageNo: number,
  pageSize: number,
  search: string,
  filters: Record<string, string[] | string>,
  sort?: { desc: boolean; id: string }[],
  accumulatedDistributionStatus: boolean = false
): Promise<GetFundsResponse> {
  const flattenFilters: Record<string, string> = {};

  Object.entries(filters || {}).forEach(([key, value]) => {
    if (!value) return;
    if (isEmpty(value)) return;
    flattenFilters[key] = Array.isArray(value) ? value.join(",") : value;
  });

  const params: Record<string, unknown> = {
    ...flattenFilters,
    page: pageNo,
    page_size: pageSize,
  };

  if (search) {
    params.search = search;
  }

  if (sort?.length) {
    params.ordering = sort.map((s) => `${s.desc ? "-" : ""}${s.id}`).join(",");
  }

  params.purchase_mode = 1;
  if (accumulatedDistributionStatus) {
    params.distribution_status = "Accumulated";
  }

  return api.get("funds/", {
    params,
  });
}

export function getFund(fundId: string): Promise<Fund> {
  return api.get(`funds/${fundId}/`);
}

export function createFund(data: Partial<Fund>): Promise<Fund> {
  return api.post("funds/", data);
}

export function updateFund(fundId: string, data: Partial<Fund>): Promise<Fund> {
  return api.put(`funds/${fundId}/`, data);
}

export async function getFundPerformance(
  fundId: string
): Promise<FundPerformance> {
  return api.get(`funds/${fundId}/detailed_performance/`);
}

export async function getFundPortfolio(fundId: string): Promise<FundPortfolio> {
  return api.get(`funds/${fundId}/portfolio/`);
}

export async function getFundDefferedLoadsManager(
  fundId: string
): Promise<FundDefferedLoadsManager> {
  return api.get(`funds/${fundId}/manager_data/`);
}

export async function getFundHoldings(
  fundId: string
): Promise<FundStockHolding[]> {
  return api.get(`funds/${fundId}/holdings/`);
}

export async function getHistoricalNav(
  fundId: string,
  startDate: string,
  endDate: string
): Promise<HistoricalNav[]> {
  return api.get(`funds/${fundId}/nav_history/`, {
    params: { start_date: startDate, end_date: endDate },
  });
}

export async function getFundFilters(fieldName: string): Promise<unknown> {
  return api.get(`funds/get_distinct_values/`, {
    params: {
      field_name: fieldName,
    },
  });
}

export async function calculateSIP(payload: {
  fund_id: string;
  start_date: string;
  end_date: string;
  sip_frequency: string;
  sip_amount?: number;
  lumpsum_amount?: number;
}): Promise<{
  cagr: number;
  standard_deviation: number;
  max_drawdown: number;
  data: Array<{
    date: string;
    total_invested: number;
    portfolio_value: number;
  }>;
}> {
  return api.post(`sip-calculator/`, payload);
}

export function fetchNFOs(): Promise<PaginatedResponse<NFO>> {
  return api.get("funds/nfo", {
    params: {
      page: 1,
      page_size: 50,
    },
  });
}

export async function fetchFundFiltersMetadata(
  fields: string[]
): Promise<FilterMetadata[]> {
  const response = await api.post<{ data: FilterMetadata[] }>(
    "funds/filters_metadata/",
    { fields }
  );

  const filterMetadata = response.data;

  const transformDistictValuesToObjectArray = filterMetadata.map((fmd) => {
    if (fmd.type !== "distinct") return fmd;
    return {
      ...fmd,
      values: fmd.values.map((v) =>
        v && typeof v === "object" ? v : { name: v, id: v }
      ),
    };
  });

  const dedupeByGroupAndLabelNames = transformDistictValuesToObjectArray.map(
    (fmd) => {
      if (fmd.type !== "distinct") return fmd;
      const { values } = fmd;
      const groupedByGroupAndLabelNames = values.reduce(
        (acc, v) => {
          const group = v.group || "DEFAULT_GROUP";
          const key = `${group}_${v.name}`;
          if (key in acc) {
            acc[key].id += `,${v.id}`;
          } else {
            acc[key] = v;
          }
          return acc;
        },
        {} as Record<string, DistinctFilterMetadata["values"][number]>
      );
      return {
        ...fmd,
        values: Object.values(groupedByGroupAndLabelNames),
      };
    }
  );

  return dedupeByGroupAndLabelNames;
}

export function fetchFundRanks({
  page,
  pageSize,
  filters,
}: {
  page?: number;
  pageSize?: number;
  filters?: Record<string, string>;
}): Promise<PaginatedResponse<FundRanking>> {
  return api.get("fund-rankings/", {
    params: {
      page,
      page_size: pageSize,
      ...filters,
    },
  });
}

export function fetchFundOverlapMatrix(
  funds: string[]
): Promise<FundOverlapMatrixResponse> {
  return api.get("funds/overlap/matrix/", {
    params: {
      funds: funds.join(","),
    },
  });
}
